import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Heading } from '../ui';
import { PRIVACY_POLICY_PATH } from '../../lib/privacyPolicySeo';

export interface ConsentBannerProps {
  onAcceptAll: () => void;
  onRejectOptional: () => void;
  onCustomize: () => void;
}

/** Accept and reject carry the same weight: one style for both. */
const CHOICE_CLASS = 'border-primary/70 text-primary hover:bg-primary hover:text-white';

/** Customize and Privacy policy: quieter than the choices, and alike. */
const FOOTER_LINK_CLASS =
  'rounded-lg px-2 py-2 text-sm font-normal text-neutral-600 underline-offset-4 hover:bg-transparent hover:text-neutral-800 hover:underline focus:outline-none focus:ring-2 focus:ring-accent';

/**
 * The first-visit question where the site has to ask before it measures: a
 * small card in the corner (bottom right, clear of the home page's hero button)
 * that never blocks the page, so the inquiry form works whether or not anyone
 * answers it.
 */
export function ConsentBanner({ onAcceptAll, onRejectOptional, onCustomize }: ConsentBannerProps) {
  const ref = useRef<HTMLElement>(null);

  // Reserve the card's height at the foot of the page, so scrolling to the end
  // always brings the last thing on it (a submit button, the footer's privacy
  // link) out from under the card.
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const body = document.body;
    const previous = body.style.paddingBottom;
    const observer = new ResizeObserver(() => {
      body.style.paddingBottom = `${Math.ceil(window.innerHeight - element.getBoundingClientRect().top)}px`;
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      body.style.paddingBottom = previous;
    };
  }, []);

  return (
    <section
      ref={ref}
      aria-labelledby="consent-banner-title"
      className="animate-consent-rise fixed inset-x-3 bottom-3 z-30 rounded-surface border border-neutral-200 bg-white p-5 shadow-dialog print:hidden sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-full sm:max-w-sm"
    >
      <Heading id="consent-banner-title" level={2} size="h5" weight="semibold" color="primary">
        Your privacy choices
      </Heading>
      <p className="mt-2 text-base leading-cozy text-neutral-700">
        May I use optional cookies to understand site traffic and see whether ads lead to
        inquiries?
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button variant="secondary" size="sm" className={CHOICE_CLASS} onClick={onRejectOptional}>
          Reject optional
        </Button>
        <Button variant="secondary" size="sm" className={CHOICE_CLASS} onClick={onAcceptAll}>
          Accept all
        </Button>
      </div>
      {/* The links keep a 36px target. Their padding is taken back by the
          row's negative margins, so the text sits 12px under the buttons,
          20px above the card's edge, and in line with the content's sides. */}
      <div className="mt-1 -mx-2 -mb-2 flex items-center justify-between">
        <Button variant="ghost" size="sm" className={FOOTER_LINK_CLASS} onClick={onCustomize}>
          Customize
        </Button>
        <Link to={PRIVACY_POLICY_PATH} className={FOOTER_LINK_CLASS}>
          Privacy policy
        </Link>
      </div>
    </section>
  );
}
